/**
 * The shop's review link (MAP 249 spec §6, ruling E; ticket 06): where a review request sends a
 * customer. ServWell writes it into the email as ITS OWN built part, so the content check sets it
 * aside (`builtLinksFor`). That trust is only safe for a link that cannot become a phishing link.
 *
 * So a review link is accepted only in one of the SHAPES the review sites themselves publish
 * (`REVIEW_LINK_SHAPES`): a positive allow-list, never a list of what to refuse (Codex 06-r3: every
 * deny-list and normaliser so far had an encoding around it, a Google AMP route, a `%25` layer,
 * a lossy query decoder). Nothing in it is ever decoded, because nothing in it can be encoded:
 * no `%` at all, a short set of characters, the link exactly as the URL parser writes it, and the
 * whole path and query matched. What remains readable (the page's name or id) is handed to the
 * server, which refuses a phone number, a long number or a brand scammers pose as in it
 * (`reviewLinkRefusal`).
 *
 * The same rules run when the owner saves it AND when a review request is written: the request
 * freezes the exact link it validated (`customer_emails.review_link`), so a link changed later is
 * never trusted for an email already queued.
 *
 * Not accepted (each site publishes its pages with an id of seven or more digits, which the
 * readable-number rule cannot tell from a phone number): Angi (`…-reviews-1234567.htm`),
 * HomeAdvisor (`rated.Name.12345678.html`), Thumbtack (`…/service/123456789012345678`), Houzz
 * (`…-pf~123456789`). Google Maps place and `?cid=` links: the owner pastes Google's own review
 * link instead (Google Business Profile → "Ask for reviews").
 */

/** The longest a review link may be (the column's CHECK, and `MERGE_FIELD_MAX.link`). */
export const REVIEW_LINK_MAX = 500;

/** One accepted shape: the whole link, its readable parts captured. */
export interface ReviewLinkShape {
  site: 'Google' | 'Yelp' | 'Facebook' | 'Nextdoor' | 'BBB';
  /** What the owner pastes, as the sentence names it. */
  example: string;
  /** Anchored at both ends; each group is a readable part (a name, a slug, an id). */
  pattern: RegExp;
}

/** A page name or slug as the sites write it. */
const SLUG = '[a-z0-9]+(?:-[a-z0-9]+)*';

export const REVIEW_LINK_SHAPES: readonly ReviewLinkShape[] = [
  {
    site: 'Google',
    example: 'https://g.page/r/…/review',
    pattern: /^https:\/\/g\.page\/r\/([A-Za-z0-9_-]{6,64})(?:\/review)?$/,
  },
  {
    site: 'Google',
    example: 'https://search.google.com/local/writereview?placeid=…',
    pattern:
      /^https:\/\/search\.google\.com\/local\/writereview\?placeid=([A-Za-z0-9_-]{10,128})$/,
  },
  {
    site: 'Google',
    example: 'https://maps.app.goo.gl/…',
    pattern: /^https:\/\/maps\.app\.goo\.gl\/([A-Za-z0-9]{6,32})$/,
  },
  {
    site: 'Yelp',
    example: 'https://www.yelp.com/biz/…',
    pattern: new RegExp(`^https://www\\.yelp\\.com/biz/(${SLUG})$`),
  },
  {
    site: 'Yelp',
    example: 'https://www.yelp.com/writeareview/biz/…',
    pattern: /^https:\/\/www\.yelp\.com\/writeareview\/biz\/([A-Za-z0-9_-]{10,40})$/,
  },
  {
    site: 'Facebook',
    example: 'https://www.facebook.com/…/reviews',
    // A page's username: letters, digits and dots, at least five; never a script (`….php`).
    pattern:
      /^https:\/\/www\.facebook\.com\/((?![^/]*\.php)[A-Za-z0-9.]{5,80})\/reviews\/?$/,
  },
  {
    site: 'Nextdoor',
    example: 'https://nextdoor.com/pages/…',
    pattern: new RegExp(`^https://nextdoor\\.com/pages/(${SLUG})/?$`),
  },
  {
    site: 'BBB',
    example: 'https://www.bbb.org/us/…/profile/…',
    pattern: new RegExp(
      `^https://www\\.bbb\\.org/us/([a-z]{2})/(${SLUG})/profile/(${SLUG})/(${SLUG})(?:/customer-reviews)?/?$`,
    ),
  },
];

/** What a link in none of the shapes is told: the forms that ARE accepted. */
export const REVIEW_LINK_FORMS =
  'Paste your Google review link (g.page/r/… or search.google.com/local/writereview?placeid=…), or your Yelp, Facebook, Nextdoor or BBB review page.';

/** The characters a review link may hold: no `%`, no `#`, no `@`, no `+`, no `&`. */
const LINK_CHARACTERS = /^[A-Za-z0-9._:/?=-]+$/;

export type ReviewLinkReading =
  | { problem: string }
  | {
      site: ReviewLinkShape['site'];
      /** The readable parts (a page's name, a slug, an id), for the server's content rules. */
      readable: string[];
    };

/**
 * `raw` (trimmed) read against the accepted shapes: the site and the readable parts, or the
 * sentence the owner reads. An empty link is "no link" and is not read here.
 */
export function readReviewLink(raw: string): ReviewLinkReading {
  const link = raw.trim();
  if (link.length > REVIEW_LINK_MAX) {
    return {
      problem: `Your review link is longer than ${REVIEW_LINK_MAX} characters. ${REVIEW_LINK_FORMS}`,
    };
  }
  if (!LINK_CHARACTERS.test(link)) {
    return {
      problem: `Your review link has characters a review page’s link doesn’t (a %-code, a space, #, @, + or &). ${REVIEW_LINK_FORMS}`,
    };
  }
  let href: string;
  try {
    href = new URL(link).href;
  } catch {
    return { problem: `That isn’t a web link. ${REVIEW_LINK_FORMS}` };
  }
  // Exactly as the URL parser writes it: no case, dot-segment or default-port difference.
  if (href !== link) {
    return {
      problem: `Paste the link exactly as your review site shows it. ${REVIEW_LINK_FORMS}`,
    };
  }
  for (const shape of REVIEW_LINK_SHAPES) {
    const m = shape.pattern.exec(link);
    if (m)
      return { site: shape.site, readable: m.slice(1).filter((p) => p !== undefined) };
  }
  return { problem: REVIEW_LINK_FORMS };
}

/**
 * Why `raw` can't be the shop's review link, as the sentence the owner reads, or null: its SHAPE
 * only (the screen says it as the owner types). The server's `reviewLinkRefusal` also reads the
 * readable parts.
 */
export function reviewLinkProblem(raw: string): string | null {
  const r = readReviewLink(raw);
  return 'problem' in r ? r.problem : null;
}
